import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import routes from '../config/routes'
import Link from 'next/link'
import onlyGuest from '../hoc/onlyGuest'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import { useForm } from 'react-hook-form'
import { HeroIcons } from '../lib/utils/heroIcons'
import userService from '../services/user.service'
import { AxiosError } from 'axios'
import LanguageSelect from '../components/LanguageSelect'

function ForgotPasswordPage() {
  const { t } = useTranslation('forgot-password')
  const [success, setSuccess] = useState<string>()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ email: string }>({ mode: 'onBlur' })

  const onSubmitForm = async (
    data: { email: string },
    e?: React.BaseSyntheticEvent,
  ): Promise<void> => {
    try {
      e?.preventDefault()
      await userService.forgotPassword(data.email)
      setSuccess(t('success.mailSent'))
      reset()
    } catch (e: any | AxiosError) {}
  }

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />

      <div className="flex min-h-screen items-center bg-purpleGradient px-3 py-10">
        <div className="flex min-h-full flex-1 flex-col justify-center sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img
              className="mx-auto h-8 w-auto"
              src="/img/logos/shelters/logotype-white.svg"
              alt="Shelters"
            />
          </div>

          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
            <div className="py-2">
              <Link href={routes.index}>
                <div className="flex font-bold text-neutralWhite">
                  <HeroIcons.ArrowLeftIcon
                    className="mr-2 h-5 w-5 items-center pt-0.5"
                    aria-hidden="true"
                  />
                  {t('backToMainMenu')}
                </div>
              </Link>
            </div>
            <div className="rounded-lg bg-white px-6 py-6 shadow sm:px-12">
              <div className="pb-6">
                <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-neutralBlack">
                  {t('title')}
                </h2>
                <p className="pt-3 text-center">{t('subtitle')}</p>
              </div>

              <form
                className="space-y-6"
                onSubmit={handleSubmit(onSubmitForm)}
                name="ForgotPasswordApp"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    {t('inputs.email')}
                  </label>
                  <div className="mt-2">
                    <input
                      className="block w-full rounded-md border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      type="email"
                      required={true}
                      autoComplete="email"
                      aria-required="true"
                      placeholder=""
                      id="email"
                      {...register('email', {
                        required: t('email') || '',
                      })}
                    />
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    {t('buttons.submit')}
                  </button>
                  {success && (
                    <div
                      className="relative py-2 text-xs text-green-700"
                      role="alert"
                    >
                      {success}
                    </div>
                  )}
                </div>
              </form>
            </div>

            <p className="mt-10 text-center text-sm text-neutralWhite">
              {t('backTo')}{' '}
              <Link
                href={routes.login}
                className="font-semibold leading-6 text-supportingPurple hover:text-brandDarkPurple"
              >
                {t('buttons.login')}
              </Link>
            </p>

            <div className="mt-12 flex items-center">
              <div className="mx-auto rounded-2xl bg-neutralWhite">
                <LanguageSelect />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default onlyGuest(ForgotPasswordPage)

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'forgot-password'])),
    },
  }
}
